import React, { useCallback, useEffect, useMemo, useState } from "react";
import classnames from 'classnames';
import { useTracking } from 'react-tracking';
import Icon from '../../../../../../../libraries/utils/components/icon';
import DropdownLink from '../../../../../common/dropdown-link';
import { ALL, TAGS } from '../../tags/moderationTags';
import { TagData } from '../../tags/moderationTagsTypes';
import { useTags } from '../../tags/useTags';
import {
  AnyTag,
  ContentType,
  Folder,
  IPageInfo,
  MessagePublishedStatus,
  MessageType,
  ModeratedBy,
  Tag
} from '../../../state/types';
import { useSendEvent } from '../../../../../../tracking/useSendEvent';

const MESSAGE_TYPES = [
  { value: MessageType.ALL, text: 'All' },
  { value: MessageType.COMMENT, text: 'Comments' },
  { value: MessageType.POST, text: 'Posts' },
  { value: MessageType.REPLY, text: 'Replies' }
];

const MESSAGE_PUBLISHED_STATUS = [
  { value: MessagePublishedStatus.ALL, text: 'All' },
  { value: MessagePublishedStatus.PUBILSHED, text: 'Published' },
  { value: MessagePublishedStatus.NOT_PUBILSHED, text: 'Not Published' }
];

const MODERATED_BY_OPTIONS = [
  { value: ModeratedBy.ALL, text: 'All' },
  { value: ModeratedBy.ME, text: 'Only Me' }
];

// Utility function to get the tag's text
export function getTagText(value: string, tags: Array<TagData> = TAGS): string {
  return tags.find(tagItem => tagItem.value === value)?.text || '';
}

const getFilterText = (
  tag: AnyTag[] | undefined,
  messageType: MessageType,
  published: MessagePublishedStatus,
  filteringByPublished: boolean,
  tags: Array<TagData>
): string => {
  const isTagAll = tag?.includes('all');
  const isMessageTypeAll = messageType === MessageType.ALL;
  const isPublishedAll = filteringByPublished ? published === MessagePublishedStatus.ALL : true;

  if (isTagAll && isMessageTypeAll && isPublishedAll) {
    return 'Filter';
  }

  if (tag.length === 1 && !isTagAll && isMessageTypeAll && isPublishedAll) {
    return `Filtered (${getTagText(tag[0], tags)})`;
  }

  if (!isPublishedAll && isTagAll && isMessageTypeAll) {
    return `Filtered (${published === 'true' ? 'published' : 'Not Published'})`;
  }

  if (!isMessageTypeAll && isTagAll && isPublishedAll) {
    return `Filtered (${messageType})`;
  }

  return 'Filtered';
};

// Utility functions to get clickable items
function getAllClickableTagItems(tags: Array<TagData>) {
  return tags
    .filter(tag => tag.value !== Tag.REJECTED_WORDS && tag.value !== Tag.PUBLISH_AND_MODERATE_WORDS)
    .sort((a, b) => {
      if (a.value === ALL) {
        return -1;
      }

      if (b.value === ALL) {
        return 1;
      }

      return a.text?.localeCompare(b.text);
    });
}

function getAllClickableMessageTypeItems(contentType: ContentType) {
  return MESSAGE_TYPES.filter(({ value }) => value !== MessageType.POST || contentType === 'POSTS');
}

export interface Props {
  pageInfo: IPageInfo;
  contentType: ContentType;
  changePageInfo: (props: Partial<IPageInfo>) => void;
}

interface ClickableItem {
  value: string;
  text: string;
}

const ModerationTagsTooltip: React.FC<Props> = ({ pageInfo, changePageInfo, contentType }) => {
  const { tag, published, messageType, sortBy, folder, moderatedBy } = pageInfo;
  const tracking = useTracking();
  const [arrayClickedTagItem, setTagItems] = useState<AnyTag[]>(['all']);
  const [onMessageType, setOnMessageType] = useState<MessageType>(MessageType.ALL);
  const [onStatusMessage, setStatusMessage] = useState<MessagePublishedStatus>(
    MessagePublishedStatus.ALL
  );
  const [selectedModeratedBy, setSelectedModeratedBy] = useState<ModeratedBy>(ModeratedBy.ALL);

  const tags = useTags();

  const filteringByPublished = folder === Folder.PENDING || folder === Folder.ALL;
  const tagFilterText = getFilterText(tag, messageType, published, filteringByPublished, tags);
  const className = classnames('filter-dropdown', { filtered: tagFilterText !== 'Filter' });

  const { sendEvent } = useSendEvent();

  const clickableTags = useMemo(() => getAllClickableTagItems(tags), [tags]);

  useEffect(() => {
    setTagItems(tag);
    setOnMessageType(messageType);
    setStatusMessage(published);
    setSelectedModeratedBy(moderatedBy);
  }, [tag, published, messageType, moderatedBy]);

  // Event handlers
  const onTagItemClick = (value: AnyTag) => {
    if (value === Tag.AI_MODERATED) {
      sendEvent({ type: 'click_ai_moderation_filter', source: 'ai_moderation' });
    }

    let updatedItems;
    tracking.trackEvent({
      eventName: 'moderation filter - moderation tag clicked',
      filter_type: value
    });

    if (arrayClickedTagItem.includes(value)) {
      updatedItems = arrayClickedTagItem.filter(clicked => clicked !== value);
    } else if (value === 'all' || arrayClickedTagItem.includes('all')) {
      updatedItems = [value];
    } else {
      updatedItems = [...arrayClickedTagItem, value];
    }
    const updatedTagValue = updatedItems.length ? updatedItems : ['all'];
    setTagItems(updatedTagValue);
  };
  const onClickStatusMessage = (value: MessagePublishedStatus) => {
    setStatusMessage(value);
  };
  const onMessageTypeClick = (value: MessageType) => {
    tracking.trackEvent({
      eventName: 'moderation filter - message type clicked',
      filter_type: value
    });
    setOnMessageType(value);
  };
  const handleApply = () => {
    changePageInfo({
      published: onStatusMessage,
      messageType: onMessageType,
      tag: arrayClickedTagItem,
      moderatedBy: selectedModeratedBy
    });
  };
  const handleClear = () => {
    setStatusMessage(MessagePublishedStatus.ALL);
    setOnMessageType(MessageType.ALL);
    setTagItems(['all']);
    setSelectedModeratedBy(ModeratedBy.ALL);

    changePageInfo({
      published: MessagePublishedStatus.ALL,
      messageType: MessageType.ALL,
      tag: ['all'],
      moderatedBy: ModeratedBy.ALL
    });
  };
  // Generate arrays of items for rendering
  const generateItems = (
    title: string,
    clickableItems: ClickableItem[],
    onClickItem: (value: string) => void,
    iconCondition: (value: string) => boolean,
    disableForSafest = false
  ): JSX.Element[] => {
    const grayOut = sortBy === 'safest' && disableForSafest;
    return [
      <h5 className={`droplist-title`} key={`${title}-subtitle`}>
        {title}
      </h5>,
      ...clickableItems.map(({ value, text }) => (
        <span
          className={classnames('tag-tooltip-item', {
            'gray-out': grayOut
          })}
          key={value}
          onClick={() => onClickItem(value)}
        >
          {text}
          {iconCondition(value) && <Icon className="selected-tag-icon" name="validation-success" />}
        </span>
      ))
    ];
  };

  // Prepare arrays of items for rendering
  const messagePublishedStatus = filteringByPublished
    ? generateItems(
        'Message Status',
        MESSAGE_PUBLISHED_STATUS,
        onClickStatusMessage,
        value => value === onStatusMessage
      )
    : [];

  const messageTypeItems = generateItems(
    'Message Types',
    getAllClickableMessageTypeItems(contentType),
    onMessageTypeClick,
    value => value === onMessageType
  );

  const moderatedByItems =
    folder !== Folder.PENDING
      ? generateItems(
          'Moderated By',
          MODERATED_BY_OPTIONS,
          value => setSelectedModeratedBy(value as ModeratedBy),
          value => value === selectedModeratedBy,
          true
        )
      : [];

  const tagItems = generateItems(
    'Moderation Tags',
    clickableTags,
    onTagItemClick,
    (value: AnyTag) => arrayClickedTagItem.includes(value),
    true
  );

  return (
    <div className={'filter-tooltip'}>
      <DropdownLink
        className={className}
        text={tagFilterText}
        items={[...messagePublishedStatus, ...messageTypeItems, ...moderatedByItems, ...tagItems]}
        clickClear={handleClear}
        clickApply={handleApply}
      >
        <Icon name="filter" className="filter-icon" />
      </DropdownLink>
    </div>
  );
};

export default ModerationTagsTooltip;
